"use client";

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

/**
 * ONE cache entry per communication body, shared by every surface that shows
 * it (timeline row, detail modal, message/email readers).
 *
 * Both callers used to keep the body in component state, so every mount — a
 * timeline re-render, reopening a detail modal — re-fetched the same S3 object.
 * A body is effectively immutable once written (a rewrite changes the route's
 * ETag, and the browser revalidates against it), so it is held for a day.
 */
export const COMMUNICATION_BODY_STALE_TIME_MS = 24 * 60 * 60 * 1000;
const COMMUNICATION_BODY_GC_TIME_MS = 30 * 60 * 1000;

export function communicationBodyQueryKey(communicationId: string) {
  return ["communication-body", communicationId] as const;
}

/** `useAuth().getAuthToken` is synchronous in @west-tennessee-consulting/ui; accept either. */
export type CommunicationBodyTokenGetter = () =>
  | string
  | null
  | undefined
  | Promise<string | null | undefined>;

/** Raw `body` from `GET /api/communications/[id]/body` (null when absent). */
export async function fetchCommunicationBody(
  communicationId: string,
  getAuthToken?: CommunicationBodyTokenGetter
): Promise<string | null> {
  const token = getAuthToken ? await getAuthToken() : null;
  // The route accepts a Bearer token OR the session cookie; send both so the
  // cache entry is valid whichever surface filled it.
  const res = await fetch(`/api/communications/${communicationId}/body`, {
    credentials: "include",
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  });
  if (!res.ok) throw new Error(`Could not load the email (${res.status})`);
  const data: unknown = await res.json();
  if (typeof data === "object" && data !== null && "body" in data) {
    const body = data.body;
    return typeof body === "string" ? body : null;
  }
  return null;
}

export function useCommunicationBodyQuery(
  communicationId: string,
  options: {
    enabled: boolean;
    getAuthToken?: CommunicationBodyTokenGetter;
  }
): UseQueryResult<string | null, Error> {
  const { enabled, getAuthToken } = options;
  return useQuery<string | null, Error>({
    queryKey: communicationBodyQueryKey(communicationId),
    queryFn: () => fetchCommunicationBody(communicationId, getAuthToken),
    enabled: enabled && communicationId.length > 0,
    staleTime: COMMUNICATION_BODY_STALE_TIME_MS,
    gcTime: COMMUNICATION_BODY_GC_TIME_MS,
    // Both callers surfaced a failure immediately and offered their own
    // recovery (preview fallback / a Retry button) — keep that.
    retry: false,
    refetchOnWindowFocus: false,
  });
}
